// Replay recorder (REP-1). Drives a canonical `ReplaySession` forward, appending
// each consumed `InputFrame` and folding a `hashWorld` keyframe every
// `REP_KEYFRAME_TICKS` (the start tick is always a keyframe — the seek/verify
// anchor). `finish()` yields a `.bbreplay` `Replay`. Also here: `InstantRing`, the
// always-on last-`REP_INSTANT_SECONDS` input ring the player can pop straight into
// the viewer (REP-4). Pure: no DOM, no transport.
import type { Registries } from '../registry/load';
import { REP, TICK } from '../config';
import { ReplaySession, type SessionInit } from './session';
import type { InputFrame, Keyframe, Replay, Snapshot } from './format';

export class Recorder {
  readonly session: ReplaySession;
  private readonly frames: InputFrame[] = [];
  private readonly keyframes: Keyframe[] = [];
  private readonly startTick: number;
  private tick = 0; // frames recorded so far (ticks since start)
  private readonly seed: string;
  private readonly options: SessionInit['options'];
  private readonly spawn: SessionInit['spawn'];
  private readonly startSnapshot?: Snapshot;

  constructor(reg: Registries, init: SessionInit & { startTick?: number }) {
    this.session = ReplaySession.create(reg, init);
    this.startTick = init.startTick ?? init.snapshot?.tickCount ?? 0;
    this.seed = init.seed;
    this.options = init.options;
    this.spawn = init.spawn;
    this.startSnapshot = init.snapshot;
    // The start state is the first keyframe (the anchor for seek + drift checks).
    this.keyframes.push({ tick: this.startTick, hash: this.session.hash() });
  }

  /** Record one consumed frame: advance the session, append the frame, and fold a
   *  keyframe if this tick lands on a `REP_KEYFRAME_TICKS` boundary. */
  record(frame: InputFrame): void {
    this.session.step(frame);
    this.frames.push(frame);
    this.tick++;
    if (this.tick % REP.KEYFRAME_TICKS === 0) {
      this.keyframes.push({ tick: this.startTick + this.tick, hash: this.session.hash() });
    }
  }

  /** Frames recorded so far. */
  get length(): number {
    return this.frames.length;
  }

  /** Assemble the finished recording (a fresh copy of the frame/keyframe arrays). */
  finish(): Replay {
    const rep: Replay = {
      version: REP.FORMAT_VERSION,
      seed: this.seed,
      options: this.options,
      startTick: this.startTick,
      spawn: this.spawn,
      frames: this.frames.slice(),
      keyframes: this.keyframes.slice(),
    };
    if (this.startSnapshot) rep.startSnapshot = this.startSnapshot;
    return rep;
  }
}

interface RingSegment {
  startTick: number;
  snapshot: Snapshot;
  hash: string;
  frames: InputFrame[];
}

/**
 * The always-on instant-replay ring (REP-4): observes live play frame-by-frame but
 * only retains the last `REP_INSTANT_SECONDS`. It keeps two rolling segments, each
 * with the world snapshot at its start; a new segment is opened every window and the
 * oldest is dropped, so retained history is always between one and two windows — at
 * least `REP_INSTANT_SECONDS` of replayable input at bounded cost. `build()` emits a
 * self-contained `Replay` (start snapshot + frames + per-segment keyframes) the
 * player can run without any network or the original world.
 */
export class InstantRing {
  private readonly session: ReplaySession;
  private readonly seed: string;
  private readonly options: SessionInit['options'];
  private readonly spawn: SessionInit['spawn'];
  private readonly windowTicks: number;
  private readonly origin: number; // absolute tick the ring's session started at
  private tick = 0; // frames observed since `origin`
  private segments: RingSegment[];

  constructor(reg: Registries, init: SessionInit) {
    this.session = ReplaySession.create(reg, init);
    this.seed = init.seed;
    this.options = init.options;
    this.spawn = init.spawn;
    this.windowTicks = Math.max(1, Math.round(REP.INSTANT_SECONDS * TICK.TPS));
    this.origin = init.snapshot?.tickCount ?? 0;
    this.segments = [{ startTick: this.origin, snapshot: this.session.snapshot(), hash: this.session.hash(), frames: [] }];
  }

  /** Observe one live frame; rotate the ring on a window boundary. */
  observe(frame: InputFrame): void {
    this.session.step(frame);
    this.tick++;
    this.segments[this.segments.length - 1]!.frames.push(frame);
    if (this.tick % this.windowTicks === 0) {
      // Absolute tick label — stable across ring rotations (drop-oldest below).
      const startTick = this.origin + this.tick;
      this.segments.push({ startTick, snapshot: this.session.snapshot(), hash: this.session.hash(), frames: [] });
      if (this.segments.length > 2) this.segments.shift();
    }
  }

  /** Ticks of replayable input currently retained (≥ one window once warmed up). */
  get retained(): number {
    return this.segments.reduce((n, s) => n + s.frames.length, 0);
  }

  /** Snapshot the retained ring into a self-contained `Replay` (REP-4 pop-in). */
  build(): Replay {
    const base = this.segments[0]!;
    const frames = this.segments.flatMap((s) => s.frames);
    return {
      version: REP.FORMAT_VERSION,
      seed: this.seed,
      options: this.options,
      startTick: base.startTick,
      spawn: this.spawn,
      frames,
      keyframes: this.segments.map((s) => ({ tick: s.startTick, hash: s.hash })),
      startSnapshot: base.snapshot,
    };
  }
}
